import React, { FC, useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Container from 'react-bootstrap/Container';
import RootStoreContext from '../../../model/RootStoreContext';
import { GameEnvironmentSelector } from '../common/GameEnvironmentSelector';
import { ExperimentsTable } from './ExperimentsTable';
import { useNavigate } from 'react-router-dom';
import { YakofficePagination } from '../../components/yakoffice-pagination/YakofficePagination';
import { YakofficePageSize } from '../../components/yakoffice-pagesize/YakofficePageSize';
import { Form } from 'react-bootstrap';
import { ClaimType, ExperimentStatus } from '@yakoffice/publisher-types';
import { ExperimentStatusToggleDropDownButton } from './ExperimentStatusToggleDropDownButton';
import { ExperimentsPerPageKey, useExperimentsSearch } from './hooks/useExperimentsSearch';
import { IGameEnvironment } from '../../../model/gameEnvironment/GameEnvironment';
import { GameVersionSelector } from '../common/GameVersionSelector';
import { useAddExperimentLink, useGetExperimentsLink } from '../../routes';
import { IGameVersion } from '../../../model/gameVersion/GameVersion';
import { PublisherViewHeader } from '../../components/publisher-view-header/publisher-view-header';
import { FloatingActionButton } from '@yakoffice/floating-actions';

export const Experiments: FC = observer(() => {

    const rootStore = useContext(RootStoreContext);
    const navigate = useNavigate();
    const { query, experimentsResult, experimentsCountResult, setPageNumber, setPageSize, setNameLike, toggleStatus } =
        useExperimentsSearch({
            defaultPageSize: parseInt(window.localStorage.getItem(ExperimentsPerPageKey) ?? '10'),
            defaultStatuses: [ExperimentStatus.Draft, ExperimentStatus.Published, ExperimentStatus.Applied, ExperimentStatus.Stopped]
        });
    const [nameSearchValue, setNameSearchValue] = useState<string | null>(null);
    const getExperimentsLink = useGetExperimentsLink();
    const addExperimentLink = useAddExperimentLink();
    const onGameVersionChanged = (targetGv: IGameVersion) => navigate(getExperimentsLink({ gameVersionId: targetGv.id }));
    const onGameEnvironmentChanged = (targetGe: IGameEnvironment) => navigate(getExperimentsLink({ gameEnvironmentId: targetGe.id }));
    const handleCreateExperiment = () => navigate(addExperimentLink);

    useEffect(() => {
        if (nameSearchValue !== null && nameSearchValue !== '' && nameSearchValue !== undefined) {
            experimentsResult.experiments?.filter(experiment => experiment.name.includes(nameSearchValue));
        }
    }, [nameSearchValue]);

    return (
        rootStore.gameEnvironmentsStore.currentGameEnvironment && (
            <div id={'experiments'}>
                <PublisherViewHeader
                    title={'Experiments'}
                    customAction={
                        <>
                            <GameEnvironmentSelector
                                disabled={!rootStore.gameEnvironmentsStore.currentGameEnvironment}
                                handleGameEnvironmentChange={onGameEnvironmentChanged}
                                data-testid="ddlGameEnvironment"
                            />
                            <GameVersionSelector handleGameVersionChange={onGameVersionChanged}
                                                 data-testid="ddlGameVersion" />
                        </>
                    }
                />

                {rootStore.authStore.currentAuthUser.hasGameEnvironmentClaim(ClaimType.EditExperiments) && (
                    <FloatingActionButton
                        callback={handleCreateExperiment}
                        buttonContent={
                            <>
                                <i className="fas fa-plus font-light me-2" /> New experiment
                            </>
                        }
                    />
                )}
                <Container fluid>
                    <div className="mdcDataTableWrapper">
                        <ExperimentStatusToggleDropDownButton
                            variant="outline-primary"
                            statuses={query.statuses || []}
                            handleStatusToggle={toggleStatus}
                            nameSearch={
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Search by name"
                                    onChange={(e) => setNameSearchValue(e.currentTarget.value)}
                                    defaultValue={query.nameLike}
                                    disabled={!experimentsResult.isLoaded}
                                />
                            }
                        />
                        <ExperimentsTable experimentsResult={experimentsResult}
                                          handleCreateExperiment={handleCreateExperiment}
                                          experimentNameSearchValue={nameSearchValue ?? ''} />
                        <div className="paginationWrapper">
                            {query.pageSize && (
                                <span className="">
                                    Rows per page <YakofficePageSize pageSize={query.pageSize}
                                                                     handleSetPageSize={setPageSize} />
                                </span>
                            )}
                            {query.pageSize && query.pageNumber && !!experimentsCountResult.count && query.pageSize < experimentsCountResult.count && (
                                <YakofficePagination pageSize={query.pageSize} activePageNumber={query.pageNumber}
                                                     itemsCount={experimentsCountResult.count}
                                                     handleSetPageNumber={setPageNumber} />
                            )}
                        </div>
                    </div>
                </Container>
            </div>
        )
    );
});
